import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { DetailedKeyword, KeywordListType, ProjectKeywordsResponse } from "../../../models/ProjectKeywordsResponse";
import { MultiSelectTagPicker } from "../../_base/MultiSelectTagPicker";
import { IProjectsService } from "../../../services/NewformaApi/IProjectsService";
import { IProjectData } from "@src/models/shared/IProjectData";
import { Log, Logger } from "../../../services/Logger";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { ErrorCircle12Filled } from "@fluentui/react-icons";
import { makeStyles, tokens } from "@fluentui/react-components";

const useStyles = makeStyles({
    noOptionError: {
        display: "inline-flex",
        alignItems: "center",
        gap: tokens.spacingHorizontalXS,
        color: tokens.colorStatusWarningForeground1,
    },
});

interface KeywordsFluent9Props extends LocalizeContextProps {
    label: string;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    itemLimit?: number;

    value: DetailedKeyword[];
    onChange: (keywords: DetailedKeyword[]) => void;

    selectedProject: IProjectData | null | undefined;
    projectsService: IProjectsService;
    keywordListType: KeywordListType;
    logger: Logger;

    testid: string;
}

const KeywordsFluent9 = ({
    label,
    placeholder,
    required,
    disabled,
    itemLimit = 99,
    value,
    onChange,
    selectedProject,
    projectsService,
    keywordListType,
    logger,
    translate,
    testid,
}: KeywordsFluent9Props) => {
    const styles = useStyles();
    const [projectKeywordsResponse, setProjectKeywordsResponse] = useState<ProjectKeywordsResponse | null>(null);
    const [isLoadingKeywords, setIsLoadingKeywords] = useState(false);
    const latestKeywordsRequestRef = useRef(0);
    // Holds the full unfiltered list fetched when the project is selected
    const allKeywordsResponseRef = useRef<ProjectKeywordsResponse | null>(null);

    // Load keywords when project changes
    useEffect(() => {
        if (!selectedProject) {
            latestKeywordsRequestRef.current += 1;
            setProjectKeywordsResponse(null);
            setIsLoadingKeywords(false);
            return;
        }

        const loadInitialKeywords = async () => {
            const requestId = ++latestKeywordsRequestRef.current;
            setIsLoadingKeywords(true);
            Log.info(`Loading keywords for selected project: ${selectedProject.name}`);
            try {
                const response = await projectsService.getProjectKeywords(selectedProject.nrn, keywordListType);
                if (requestId !== latestKeywordsRequestRef.current) return;
                allKeywordsResponseRef.current = response;
                setProjectKeywordsResponse(response);
            } catch (error) {
                if (requestId !== latestKeywordsRequestRef.current) return;
                logger.error("Failed to load keywords", error);
                allKeywordsResponseRef.current = null;
                setProjectKeywordsResponse(null);
            } finally {
                if (requestId !== latestKeywordsRequestRef.current) return;
                setIsLoadingKeywords(false);
            }
        };

        void loadInitialKeywords();

        return () => {
            latestKeywordsRequestRef.current += 1;
            allKeywordsResponseRef.current = null;
        };
    }, [selectedProject, projectsService, keywordListType, logger]);

    const optionMap = useMemo(
        () =>
            new Map((projectKeywordsResponse?.items ?? []).map((keywordObject) => [keywordObject.name, keywordObject])),
        [projectKeywordsResponse?.items]
    );

    const options = useMemo(
        () =>
            [...(projectKeywordsResponse?.items ?? [])].sort(
                (a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name)
            ),
        [projectKeywordsResponse?.items]
    );

    const handleSelectionChange = (selectedNames: string[]) => {
        const selectedKeywords: DetailedKeyword[] = [];

        // Build a lookup from the current value so previously selected keywords are always
        // preserved with their original metadata, even when optionMap has been narrowed by
        // a search query that no longer includes them.
        const valueMap = new Map(value.map((kw) => [kw.name, kw]));

        for (const name of selectedNames) {
            // First: preserve already-selected keywords regardless of current search state
            const alreadySelected = valueMap.get(name);
            if (alreadySelected) {
                selectedKeywords.push(alreadySelected);
                continue;
            }

            // Second: look up in the current API response options
            const existingKeyword = optionMap.get(name);
            if (existingKeyword) {
                selectedKeywords.push(existingKeyword);
                continue;
            }

            // Last: allow custom values if the project permits
            if (projectKeywordsResponse?.allowCustom) {
                selectedKeywords.push({
                    name,
                    type: "generic",
                    displayOrder: 0,
                });
            }
        }

        onChange(selectedKeywords);
    };

    const handleInput = useCallback(
        (query: string) => {
            if (!selectedProject || !allKeywordsResponseRef.current) return;

            const all = allKeywordsResponseRef.current;
            const trimmed = query.trim().toLowerCase();
            if (!trimmed) {
                setProjectKeywordsResponse(all);
                return;
            }
            const filtered: ProjectKeywordsResponse = {
                ...all,
                items: all.items.filter((kw) => kw.name.toLowerCase().includes(trimmed)),
            };
            setProjectKeywordsResponse(filtered);
        },
        [selectedProject]
    );

    if (!selectedProject) {
        placeholder = translate("PROJECT.NO_SELECTION") as string;
    } else if (!options.length) {
        placeholder = translate("ACTION_ITEM.KEYWORD_PICKER_PLACEHOLDER_NO_KEYWORDS") as string;
    }
    if (isLoadingKeywords && !value.length) {
        placeholder = translate("ACTION_ITEM.LOADING_KEYWORDS") as string;
    }
    // Hide placeholder when options are selected
    if (value.length > 0) {
        placeholder = "";
    }

    const isCustomInputAllowed = !!projectKeywordsResponse?.allowCustom;

    let noOptionText: string | JSX.Element = "";
    if (isCustomInputAllowed) {
        noOptionText = translate("FLUENT9.PRESS_ENTER") as string;
    } else {
        noOptionText = (
            <span className={styles.noOptionError}>
                <ErrorCircle12Filled />
                {translate("FLUENT9.CUSTOM_VALUES_NOT_ALLOWED") as string}
            </span>
        );
    }

    return (
        <MultiSelectTagPicker
            label={label}
            placeholder={placeholder}
            required={required}
            disabled={disabled || !selectedProject}
            disableAutoFocusOverride={true}
            allowCustomInput={isCustomInputAllowed}
            itemLimit={itemLimit}
            options={options.map((o) => o.name)}
            selectedOptions={value.map((v) => v.name)}
            onSelectionChange={handleSelectionChange}
            onInput={handleInput}
            isLoading={isLoadingKeywords}
            testid={testid}
            noOptionText={noOptionText}
        />
    );
};

export default withLocalize(KeywordsFluent9);
